import { useApolloClient } from "@apollo/client";
import { Empty, Spin, notification } from "antd";
import { cloneDeep, get, isNil } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "shared/components/Button";
import ScreenRenderer from "shared/components/ScreenRenderer";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { persistProcessStatus } from "screens/Private/Broker/Projects/Detail/resolveProcessRuntime";
import graphql from "utils/api/graphql";
import {
  ACTION_STATUSES,
  EDGE_KINDS,
  PAYMENT_STATUS,
  PROCESS_TRIGGER_NAMES,
  RUN_STATUSES,
  SCREEN_BINDING_KINDS,
  SCREEN_EVENTS,
  STEP_STATUSES,
} from "utils/constants";
import CanvasForm from "./CanvasForm";
import {
  actionStatusRelease,
  applyCanvasRunUpdate,
  resolveHeldHopScreen,
} from "./applyCanvasRunUpdate";
import {
  MAX_SILENT_PROCESSING_HOPS,
  isPaidScreenEvent,
  isSilentProcessingNode,
  paymentResumeToken,
  pickAutoContinueChild,
  shouldPollAwaitingRun,
} from "./paymentResume";
import {
  hopPastSilentNodes,
  shouldPreviewOptimisticRun,
} from "./silentProcessing";
import {
  backTargetNodeId,
  hasEventEdge,
  isSameRunSnapshot,
  optimisticCurrentNode,
  rememberRunNode,
} from "./runNodeCache";
import { shouldKeepPublishedScreen } from "./keepPublishedScreen";
import {
  screenStaySubscriptionId,
  existingQuotedSubscriptionId,
  GO_TO_SUBSCRIPTION,
  isGoToSubscriptionBinding,
  quotedPlanId,
  quotedSubscriptionReadyAction,
  rememberStayQuotedSubscription,
} from "./quotedSubscription";
import { isReloadStepEvent } from "./screenEvents";

const AWAITING_POLL_INTERVAL = 5000;
const SETTLING_POLL_INTERVAL = 2000;

const errorText = (error) => {
  if (isNil(error)) return null;
  if (typeof error === "string") return error;
  return get(error, "message") || JSON.stringify(error);
};

const FIELD_VALIDATION_FAILED = "FIELD_VALIDATION_FAILED";

const missingFieldsText = (error, formDefinition, template) => {
  if (get(error, "code") !== FIELD_VALIDATION_FAILED) return null;
  const fields = get(formDefinition, "fields") || [];
  const labels = Object.keys(get(error, "fieldErrors") || {}).map(
    (accessor) =>
      get(
        fields.find((field) => field.accessor === accessor),
        "label",
      ) || accessor,
  );
  if (!labels.length) return null;
  return template.replace("{{fields}}", labels.join(", "));
};

const CanvasRunner = ({ project, process, onBack, onSubscriptionReady }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const RUN = t("RUN", { returnObjects: true });
  const client = useApolloClient();
  const projectId = get(project, "id");
  const processId = get(process, "id");
  const [run, setRun] = useState(null);
  const [liveProject, setLiveProject] = useState(null);
  const [startError, setStartError] = useState(null);
  const [actionStatus, setActionStatus] = useState({
    status: ACTION_STATUSES.IDLE,
  });
  const [settling, setSettling] = useState(false);
  const [processing, setProcessing] = useState(false);
  const actionInFlight = useRef(false);
  const silentHopRef = useRef(false);
  const lastPublishedVersionRef = useRef(null);
  const hopHoldRef = useRef(null);
  const nodeCacheRef = useRef(new Map());
  const startedRef = useRef(false);
  const autoContinuedRef = useRef(null);
  const notifiedSubscriptionRef = useRef(null);
  const stayQuotedLockRef = useRef(false);
  const seededSubscriptionRef = useRef(false);
  const openedQuotedRef = useRef(null);
  const runRef = useRef(null);
  runRef.current = run;

  useEffect(() => {
    setRun(null);
    setLiveProject(null);
    setStartError(null);
    setProcessing(false);
    startedRef.current = false;
    autoContinuedRef.current = null;
    notifiedSubscriptionRef.current = null;
    stayQuotedLockRef.current = false;
    seededSubscriptionRef.current = false;
    openedQuotedRef.current = null;
    silentHopRef.current = false;
    lastPublishedVersionRef.current = null;
    hopHoldRef.current = null;
  }, [projectId, processId]);

  const runId = get(run, "id");
  const runUpdatedDate = get(run, "updatedDate");
  const currentNodeId = get(run, "currentNode.id");

  useEffect(() => {
    if (isNil(currentNodeId)) return;
    persistProcessStatus(projectId, currentNodeId);
    rememberRunNode(nodeCacheRef.current, get(run, "currentNode"));
    const published = (get(run, "currentNode.screens") || []).find((screen) =>
      get(screen, "currentPublishedVersion"),
    );
    const version = get(published, "currentPublishedVersion");
    if (version) lastPublishedVersionRef.current = version;
  }, [currentNodeId, projectId, run]);

  const releaseActionStatus = useMemo(
    () =>
      actionStatusRelease({
        setActionStatus,
        isActionInFlight: () => actionInFlight.current,
      }),
    [],
  );

  useEffect(() => {
    if (isNil(currentNodeId)) return;
    releaseActionStatus();
  }, [currentNodeId, releaseActionStatus]);

  const refreshProject = useCallback(async () => {
    if (isNil(projectId)) return null;
    try {
      const { data } = await client.query({
        query: graphql.queries.PROJECT,
        variables: { where: { id: projectId } },
        fetchPolicy: "network-only",
      });
      const next = get(data, "project");
      if (!isNil(next)) setLiveProject(cloneDeep(next));
      return next;
    } catch (_error) {
      return null;
    }
  }, [client, projectId]);

  useEffect(() => {
    if (isNil(projectId) || isNil(runId)) return undefined;
    let cancelled = false;
    client
      .query({
        query: graphql.queries.PROJECT,
        variables: { where: { id: projectId } },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        if (cancelled || isNil(get(data, "project"))) return;
        setLiveProject(cloneDeep(data.project));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectId, runId, runUpdatedDate, client]);

  const currentProject = liveProject || project;

  // A project reopened on a quoted formula already carries a subscription.
  // Seeding the ref from that first snapshot keeps the advisor on the step he
  // opened instead of bouncing him into a subscription he created earlier.
  useEffect(() => {
    if (!onSubscriptionReady) return;
    // A node that opens the subscription on enter must not be swallowed
    // by the first-snapshot seed (that visit is the one we want to open).
    if (get(run, "currentNode.config.openSubscriptionOnEnter")) return;
    const subscriptionId = get(currentProject, "fields.currentSubscription");
    const next = quotedSubscriptionReadyAction({
      stayLocked: stayQuotedLockRef.current,
      seeded: seededSubscriptionRef.current,
      notifiedId: notifiedSubscriptionRef.current,
      nextId: subscriptionId,
    });
    if (!seededSubscriptionRef.current) {
      seededSubscriptionRef.current = true;
    }
    notifiedSubscriptionRef.current = next.notifiedId;
    if (!next.open) return;
    onSubscriptionReady(subscriptionId);
  }, [
    get(currentProject, "fields.currentSubscription"),
    get(run, "currentNode.config.openSubscriptionOnEnter"),
    onSubscriptionReady,
  ]);

  const [startProjectRun, { loading: starting }] = useMutation(
    graphql.mutations.START_PROJECT_RUN,
  );
  const [advanceProjectRun, { loading: advancing }] = useMutation(
    graphql.mutations.ADVANCE_PROJECT_RUN,
  );
  const [signalProjectRun, { loading: signaling }] = useMutation(
    graphql.mutations.SIGNAL_PROJECT_RUN,
  );
  const [resumeProjectRun] = useMutation(graphql.mutations.RESUME_PROJECT_RUN);
  const [updateEntity] = useMutation(graphql.mutations.UPDATE_ENTITY);
  const [executeField] = useMutation(graphql.queries.EXECUTE_FIELD);
  const [descending, setDescending] = useState(false);
  const paymentResumeInFlight = useRef(false);

  const loadRunById = useCallback(
    (id) =>
      client
        .query({
          query: graphql.queries.PROJECT_RUN,
          variables: { where: { id } },
          fetchPolicy: "network-only",
        })
        .then(({ data }) => {
          const loaded = get(data, "runNode");
          if (isNil(loaded) || isSameRunSnapshot(runRef.current, loaded)) {
            return;
          }
          setRun(loaded);
        })
        .catch(() => {}),
    [client],
  );

  const goToBranch = async (childId) => {
    setDescending(true);
    try {
      await loadRunById(childId);
    } finally {
      setDescending(false);
    }
  };

  useEffect(() => {
    const child = pickAutoContinueChild(run, {
      subscriptionId: get(currentProject, "fields.currentSubscription"),
    });
    if (!child || autoContinuedRef.current === child.id) return;
    autoContinuedRef.current = child.id;
    goToBranch(child.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, get(currentProject, "fields.currentSubscription")]);

  const startRun = () => {
    if (isNil(projectId) || isNil(processId)) return;
    setStartError(null);
    startProjectRun({
      variables: { project: { id: projectId }, process: { id: processId } },
      onCompleted: async ({ startRun: started }) => {
        if (started) {
          // Refresh first so a silent hop onto confirmation already has
          // documents written by entry connectors (attestation, contract).
          await refreshProject();
          setRun(started);
          return;
        }
        setStartError(RUN.FAILED);
      },
      onError: (error) => {
        setStartError(errorText(error) || RUN.FAILED);
      },
    });
  };

  const startRunRef = useRef(null);
  startRunRef.current = startRun;

  useEffect(() => {
    const onReloadStep = ({ data }) => {
      if (!isReloadStepEvent(data) || starting) return;
      startRunRef.current();
    };
    window.addEventListener("message", onReloadStep);
    return () => window.removeEventListener("message", onReloadStep);
  }, [starting]);

  // Always resume via startRun (not a raw run query) so missing entry
  // connectors can catch up when the page is reopened on a live step.
  useEffect(() => {
    if (starting || !isNil(run) || startedRef.current || startError) return;
    if (isNil(projectId) || isNil(processId)) return;
    startedRef.current = true;
    startRun();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [starting, run, projectId, processId, startError]);

  useEffect(() => {
    if (get(run, "status") !== RUN_STATUSES.AWAITING) {
      setSettling(false);
      return undefined;
    }
    if (
      !shouldPollAwaitingRun({
        status: get(run, "status"),
      })
    ) {
      return undefined;
    }
    const timer = setInterval(
      () => loadRunById(run.id),
      settling ? SETTLING_POLL_INTERVAL : AWAITING_POLL_INTERVAL,
    );
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [get(run, "status"), get(run, "id"), settling, loadRunById]);

  useEffect(() => {
    const onScreenEvent = async ({ data }) => {
      if (get(data, "source") !== SCREEN_EVENTS.SOURCE) return;
      if (get(data, "type") !== SCREEN_EVENTS.TYPES.PAYMENT) return;
      if (!isPaidScreenEvent(data) || paymentResumeInFlight.current) {
        const id = get(runRef.current, "id");
        if (!isNil(id)) loadRunById(id);
        return;
      }
      const current = runRef.current;
      const id = get(current, "id");
      const paidProjectId = get(current, "project.id") || projectId;
      if (isNil(id) || isNil(paidProjectId)) return;
      setSettling(true);
      paymentResumeInFlight.current = true;
      try {
        const reference =
          get(data, "reference") || `SP-${id.slice(-8).toUpperCase()}`;
        await Promise.all([
          updateEntity({
            variables: {
              project: { id: paidProjectId },
              entity: "advanced",
              field: "paymentStatus",
              value: PAYMENT_STATUS.PAID,
            },
          }),
          updateEntity({
            variables: {
              project: { id: paidProjectId },
              entity: "advanced",
              field: "paymentReference",
              value: reference,
            },
          }),
        ]);
        const { data: resumed } = await resumeProjectRun({
          variables: {
            resumeToken: paymentResumeToken(paidProjectId),
            payload: {
              paid: true,
              status: get(data, "status"),
              reference,
            },
          },
        });
        let next = get(resumed, "resumeRun");
        let hops = 0;
        while (
          isSilentProcessingNode(next) &&
          hops < MAX_SILENT_PROCESSING_HOPS
        ) {
          const { data: advanced } = await advanceProjectRun({
            variables: { run: { id: next.id }, trigger: {} },
          });
          next = get(advanced, "advanceRun");
          hops += 1;
        }
        if (!isNil(next)) {
          await refreshProject();
          setRun(next);
          setActionStatus({ status: ACTION_STATUSES.IDLE });
          setSettling(false);
          return;
        }
        await loadRunById(id);
      } catch (error) {
        if (!isNil(id)) loadRunById(id);
        notification.error({
          message: errorText(error) || t("RUN.FAILED"),
        });
      } finally {
        paymentResumeInFlight.current = false;
        setSettling(false);
      }
    };
    window.addEventListener("message", onScreenEvent);
    return () => window.removeEventListener("message", onScreenEvent);
  }, [
    loadRunById,
    projectId,
    refreshProject,
    resumeProjectRun,
    updateEntity,
    advanceProjectRun,
    t,
  ]);

  const { data: statusDataResult, refetch: refetchStatusData } = useQuery(
    graphql.queries.STATUS_DATA,
    {
      variables: { run: { id: get(run, "id") } },
      skip: isNil(get(run, "id")),
      fetchPolicy: "network-only",
    },
  );
  const statusData =
    get(statusDataResult, "statusData") || get(run, "context.data") || {};
  const statusAttachments = get(statusData, "attachments") || [];

  useEffect(() => {
    if (isNil(runId) || statusAttachments.length) return undefined;
    const timer = setTimeout(() => {
      refetchStatusData();
      if (refreshProject) refreshProject();
    }, 11000);
    return () => clearTimeout(timer);
  }, [
    currentNodeId,
    refreshProject,
    refetchStatusData,
    runId,
    statusAttachments.length,
  ]);

  const applyRunUpdate = (nextRun, options = {}) =>
    applyCanvasRunUpdate({
      nextRun,
      setRun,
      setActionStatus: releaseActionStatus,
      refetchStatusData,
      refreshProject,
      ...options,
    });

  const moveSilentForward = useCallback(
    async (current) => {
      const { data } = await advanceProjectRun({
        variables: { run: { id: current.id }, trigger: {} },
      });
      return get(data, "advanceRun");
    },
    [advanceProjectRun],
  );

  const moveSilentBack = useCallback(
    async (current) => {
      const { data } = await signalProjectRun({
        variables: { run: { id: current.id }, event: "back" },
      });
      return get(data, "signalRun");
    },
    [signalProjectRun],
  );

  const settleAfterHop = async (nextRun, direction) => {
    if (isSilentProcessingNode(nextRun)) setProcessing(true);
    const hopped = await hopPastSilentNodes({
      run: nextRun,
      direction,
      moveForward: moveSilentForward,
      moveBack: moveSilentBack,
    });
    const applied = await applyRunUpdate(hopped, {
      refreshFirst: !isSilentProcessingNode(hopped),
    });
    setProcessing(isSilentProcessingNode(hopped));
    return applied;
  };

  const advance = async (trigger) => {
    if (isNil(get(run, "id"))) return false;
    const { data } = await advanceProjectRun({
      variables: { run: { id: run.id }, trigger },
    });
    return settleAfterHop(get(data, "advanceRun"), "forward");
  };

  const signal = async (event, direction = "back") => {
    if (isNil(get(run, "id"))) return false;
    const { data } = await signalProjectRun({
      variables: { run: { id: run.id }, event },
    });
    return settleAfterHop(get(data, "signalRun"), direction);
  };

  const resolveQuotedSubscriptionId = async (planId, existingId) => {
    const known = existingQuotedSubscriptionId(
      currentProject,
      planId,
      existingId,
    );
    if (known) return known;
    if (!planId || isNil(projectId)) return "";
    try {
      const { data } = await executeField({
        variables: {
          key: "ensureQuotedSubscription",
          entity: { id: projectId },
          args: { insurancePlan: { id: planId } },
        },
      });
      return get(data, "executeField.subscription.id") || "";
    } catch (_error) {
      return existingQuotedSubscriptionId(currentProject, planId, existingId);
    }
  };

  const hopToSubscription = async () => {
    if (!hasEventEdge(runRef.current, GO_TO_SUBSCRIPTION)) return;
    try {
      await signal(GO_TO_SUBSCRIPTION, "forward");
    } catch (_error) {
      // Opening the formula does not depend on the hop target existing.
    }
  };

  const openQuotedSubscription = async (planId, existingId) => {
    const subscriptionId = await resolveQuotedSubscriptionId(
      planId,
      existingId,
    );
    await hopToSubscription();
    if (subscriptionId && onSubscriptionReady) {
      notifiedSubscriptionRef.current = subscriptionId;
      onSubscriptionReady(subscriptionId);
    }
    return subscriptionId;
  };

  useEffect(() => {
    if (!onSubscriptionReady) return undefined;
    if (!get(run, "currentNode.config.openSubscriptionOnEnter")) {
      return undefined;
    }
    const planId = get(currentProject, "fields.selectedPlan");
    if (!planId || isNil(runId) || isNil(projectId)) return undefined;
    const subscriptions = get(currentProject, "fields.subscriptions") || {};
    const existingSubscriptionId =
      subscriptions[planId] ||
      get(currentProject, "fields.currentSubscription") ||
      "";
    const openKey = `${runId}:${planId}`;
    if (openedQuotedRef.current === openKey) return undefined;
    openedQuotedRef.current = openKey;
    let cancelled = false;
    const openQuoted = async () => {
      setProcessing(true);
      try {
        const subscriptionId = await resolveQuotedSubscriptionId(
          planId,
          existingSubscriptionId,
        );
        if (cancelled || !subscriptionId) return;
        notifiedSubscriptionRef.current = subscriptionId;
        await hopToSubscription();
        if (cancelled || !onSubscriptionReady) return;
        onSubscriptionReady(subscriptionId);
      } catch (_error) {
        openedQuotedRef.current = null;
      } finally {
        if (!cancelled) setProcessing(false);
      }
    };
    openQuoted();
    return () => {
      cancelled = true;
    };
    // Open once when the advisor lands on the quoted twin.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentNodeId,
    runId,
    onSubscriptionReady,
    projectId,
    get(currentProject, "fields.selectedPlan"),
    get(run, "currentNode.config.openSubscriptionOnEnter"),
  ]);

  useEffect(() => {
    if (!isSilentProcessingNode(run) || silentHopRef.current) return undefined;
    silentHopRef.current = true;
    setProcessing(true);
    let cancelled = false;
    hopPastSilentNodes({
      run,
      moveForward: moveSilentForward,
    }).then(async (next) => {
      if (cancelled) return;
      await applyRunUpdate(next, {
        refreshFirst: !isSilentProcessingNode(next),
      });
      setProcessing(isSilentProcessingNode(next));
      silentHopRef.current = false;
    });
    return () => {
      cancelled = true;
      silentHopRef.current = false;
      setProcessing(false);
    };
    // Hop only when the current node itself is silent (no screen / no form).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNodeId, isSilentProcessingNode(run)]);

  // A screen that opens a subscription itself passes `stay: true`. Any other
  // executor result still lets the broker subscription screen open.
  const handleFieldExecuted = (detail) => {
    rememberStayQuotedSubscription(
      notifiedSubscriptionRef,
      screenStaySubscriptionId(detail),
    );
  };

  const handleScreenAction = async (binding) => {
    if (isGoToSubscriptionBinding(binding)) {
      await openQuotedSubscription(
        quotedPlanId(binding, currentProject),
        get(binding, "item.subscriptionId"),
      );
      return;
    }
    if (get(binding, "kind") !== SCREEN_BINDING_KINDS.PROCESS_TRIGGER) return;
    const triggerName = get(binding, "name");
    if (actionInFlight.current) return;
    actionInFlight.current = true;
    const departingScreen = (get(run, "currentNode.screens") || []).find(
      (screen) => screen.currentPublishedVersion,
    );
    hopHoldRef.current = {
      version:
        lastPublishedVersionRef.current ||
        get(departingScreen, "currentPublishedVersion"),
      formDefinition: get(run, "currentNode.formDefinition"),
    };
    setActionStatus({ name: triggerName, status: ACTION_STATUSES.LOADING });
    let failed = false;
    try {
      if (triggerName === PROCESS_TRIGGER_NAMES.BACK) {
        const preview = optimisticCurrentNode(
          run,
          backTargetNodeId(run),
          nodeCacheRef.current,
        );
        if (shouldPreviewOptimisticRun(preview)) setRun(preview);
        else if (preview && !lastPublishedVersionRef.current) {
          setProcessing(true);
        }
        const moved = await signal("back");
        if (!moved) {
          setRun(run);
        }
        return;
      }
      if (triggerName !== PROCESS_TRIGGER_NAMES.NEXT) {
        const selectedPlanId =
          get(binding, "insurancePlanId") ||
          get(currentProject, "fields.selectedPlan");
        let executed = null;
        try {
          const { data } = await executeField({
            variables: {
              key: triggerName,
              entity: { id: projectId },
              args: selectedPlanId
                ? {
                    project: { id: projectId },
                    insurancePlan: { id: selectedPlanId },
                  }
                : { project: { id: projectId } },
            },
          });
          executed = get(data, "executeField");
        } catch (error) {
          const graphQLMessage =
            get(error, "graphQLErrors.0.message") ||
            get(error, "message") ||
            "";
          if (graphQLMessage.indexOf("Unknown field executor") === -1) {
            throw error;
          }
        }
        // Remember the quoted subscription so a later project refresh does
        // not treat it as a new one and open the subscription screen.
        const subscription = get(executed, "subscription");
        rememberStayQuotedSubscription(
          notifiedSubscriptionRef,
          get(subscription, "id"),
        );
        if (get(binding, "stay") === true) {
          stayQuotedLockRef.current = true;
          try {
            const nextProject = await refreshProject();
            rememberStayQuotedSubscription(
              notifiedSubscriptionRef,
              get(nextProject, "fields.currentSubscription"),
            );
            // Stay actions only flip the tagged twin (QUOTE_SENT). Opening
            // the subscription is a second click on Accéder.
            if (hasEventEdge(run, triggerName)) {
              await signal(triggerName, "forward");
            }
          } finally {
            stayQuotedLockRef.current = false;
          }
          return;
        }
      }
      // updateEntity already persisted drafts. The engine flushes those
      // drafts onto entities when the run leaves this node.
      const forwardEdges = (
        get(run, "currentNode.possibleStatuses") || []
      ).filter((edge) => get(edge, "kind") !== EDGE_KINDS.EVENT);
      if (forwardEdges.length === 1) {
        const preview = optimisticCurrentNode(
          run,
          get(forwardEdges[0], "nextStatus"),
          nodeCacheRef.current,
        );
        if (shouldPreviewOptimisticRun(preview)) setRun(preview);
        else if (preview && !lastPublishedVersionRef.current) {
          setProcessing(true);
        }
      }
      const moved = await advance({});
      if (!moved) {
        setProcessing(false);
        return;
      }
    } catch (error) {
      failed = true;
      setActionStatus({ name: triggerName, status: ACTION_STATUSES.ERROR });
      setProcessing(false);
      notification.error({
        message: errorText(error) || RUN.FAILED,
      });
    } finally {
      actionInFlight.current = false;
      hopHoldRef.current = null;
      if (!failed) {
        setActionStatus({ status: ACTION_STATUSES.IDLE });
      }
    }
  };

  if (isNil(projectId) || isNil(processId))
    return <Empty description={RUN.NO_PROCESS} />;
  if (isNil(run)) {
    if (startError) {
      return (
        <div className="canvas-run__center">
          <div className="canvas-run__error">{startError}</div>
          <Button type="primary" onClick={startRun} disabled={starting}>
            {RUN.START}
          </Button>
        </div>
      );
    }
    return (
      <div className="canvas-run__loading">
        <Spin />
      </div>
    );
  }

  const status = run.status;
  const currentNode = run.currentNode || {};
  const formFields = get(currentNode, "formDefinition.fields") || [];
  const publishedScreen = (get(currentNode, "screens") || []).find(
    (screen) => screen.currentPublishedVersion,
  );
  const publishedVersion = get(publishedScreen, "currentPublishedVersion");
  const steps = run.steps || [];
  const activeChildren = (run.children || []).filter((child) =>
    [RUN_STATUSES.RUNNING, RUN_STATUSES.AWAITING, RUN_STATUSES.FAILED].includes(
      child.status,
    ),
  );
  const failedStep = [...steps]
    .reverse()
    .find((step) => step.status === STEP_STATUSES.FAILED);
  const awaitingStep = [...steps]
    .reverse()
    .find((step) => step.status === STEP_STATUSES.AWAITING);
  const eventEdges = (get(currentNode, "possibleStatuses") || []).filter(
    (edge) => get(edge, "kind") === EDGE_KINDS.EVENT && get(edge, "event"),
  );

  const serverFieldErrors = get(failedStep, "error.fieldErrors") || null;
  const failedStepText =
    missingFieldsText(
      get(failedStep, "error"),
      currentNode.formDefinition,
      RUN.MISSING_FIELDS,
    ) || errorText(get(failedStep, "error"));
  const backEdge = eventEdges.find((edge) => get(edge, "event") === "back");
  const forwardEdges = (get(currentNode, "possibleStatuses") || []).filter(
    (edge) => get(edge, "kind") !== EDGE_KINDS.EVENT,
  );
  const isLastStep = !forwardEdges.length;
  const showProcessing = processing || isSilentProcessingNode(run);
  const hopBusy =
    actionStatus.status === ACTION_STATUSES.LOADING || showProcessing;
  const heldScreen = resolveHeldHopScreen({
    hopBusy,
    hopHold: hopHoldRef.current,
    publishedVersion,
    processingVersion: showProcessing ? lastPublishedVersionRef.current : null,
    currentFormDefinition: currentNode.formDefinition,
  });
  const heldVersion = heldScreen.version;
  const heldFormDefinition = heldScreen.formDefinition;

  const renderStep = () => {
    if (!isNil(heldVersion))
      return (
        <ScreenRenderer
          version={heldVersion}
          project={currentProject}
          requester={get(run, "requester")}
          data={statusData}
          formDefinition={heldFormDefinition}
          onAction={showProcessing ? undefined : handleScreenAction}
          onFieldExecuted={handleFieldExecuted}
          busy={hopBusy}
          processId={processId}
          progress={get(currentNode, "progress")}
        />
      );
    if (formFields.length)
      return (
        <CanvasForm
          formDefinition={currentNode.formDefinition}
          project={currentProject}
          data={statusData}
          advance={advance}
          onBack={backEdge ? () => signal("back") : undefined}
          showBack={!!backEdge}
          isLastStep={isLastStep}
          fieldErrors={serverFieldErrors}
          processId={processId}
        />
      );
    if (showProcessing) return null;
    return (
      <Button type="primary" onClick={() => advance({})} disabled={advancing}>
        {RUN.NEXT}
      </Button>
    );
  };

  const keepPublishedScreen = shouldKeepPublishedScreen({
    heldVersion,
    status,
    showProcessing,
    hasFormFields: formFields.length > 0,
  });

  if (keepPublishedScreen) {
    return (
      <div className="canvas-run__step">
        <div className="canvas-run__error-slot">
          {status === RUN_STATUSES.FAILED ? failedStepText || RUN.FAILED : null}
        </div>
        {renderStep()}
      </div>
    );
  }

  const autoContinueChild = pickAutoContinueChild(run, {
    subscriptionId: get(currentProject, "fields.currentSubscription"),
  });
  if (status === RUN_STATUSES.COMPLETED && (autoContinueChild || descending)) {
    return (
      <div className="canvas-run__loading">
        <Spin />
      </div>
    );
  }

  if (status === RUN_STATUSES.COMPLETED)
    return (
      <div className="canvas-run__center">
        <div className="canvas-run__success">{RUN.COMPLETED}</div>
        {activeChildren.map((child) => (
          <Button
            key={child.id}
            type="primary"
            onClick={() => goToBranch(child.id)}
            disabled={descending}
            className="mt-12"
          >
            {`${RUN.CONTINUE_TO} ${
              get(child, "currentNode.name") || child.subjectType
            }`}
          </Button>
        ))}
      </div>
    );

  if (status === RUN_STATUSES.CANCELLED)
    return <div className="canvas-run__center">{RUN.CANCELLED}</div>;

  if (status === RUN_STATUSES.FAILED)
    return (
      <div className="canvas-run__center">
        <div className="canvas-run__error">{failedStepText || RUN.FAILED}</div>
        {formFields.length ? (
          renderStep()
        ) : (
          <Button
            type="primary"
            onClick={() => advance({})}
            disabled={advancing}
          >
            {RUN.RETRY}
          </Button>
        )}
      </div>
    );

  if (status === RUN_STATUSES.AWAITING)
    return (
      <div className="canvas-run__center">
        <div className="canvas-run__awaiting-row">
          <Spin size="small" />
          <span>
            {get(awaitingStep, "output.reason") ||
              get(awaitingStep, "nodeType") ||
              RUN.WAITING}
          </span>
        </div>
        {eventEdges.length ? (
          <div className="canvas-run__row-wrap">
            {eventEdges.map((edge) => (
              <Button
                key={edge.id}
                type="primary--outlined"
                onClick={() => {
                  if (edge.event === GO_TO_SUBSCRIPTION) {
                    handleScreenAction({
                      action: GO_TO_SUBSCRIPTION,
                    });
                    return;
                  }
                  signal(edge.event);
                }}
                disabled={signaling}
              >
                {get(RUN, `EVENTS.${edge.event}`) || edge.event}
              </Button>
            ))}
          </div>
        ) : null}
        {!eventEdges.length &&
        (!isNil(publishedVersion) || formFields.length) ? (
          <div className="mt-16">{renderStep()}</div>
        ) : null}
      </div>
    );

  if (showProcessing) {
    return (
      <div className="canvas-run__processing canvas-run__processing--page">
        <Spin size="large" />
        <p className="canvas-run__processing-copy">{RUN.PROCESSING}</p>
      </div>
    );
  }

  return (
    <div className="canvas-run__form">
      {currentNode.name ? (
        <h2 className="canvas-run__title">{currentNode.name}</h2>
      ) : null}
      {renderStep()}
    </div>
  );
};

export default CanvasRunner;
